import { AddressSubject } from "@omni/types/api/customer/cloud/shared/enums/addressSubject.enum";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Country } from "@omni/types/enums/country.enum";
import {
    createContext,
    lazy,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import { clearMessageById } from "@/redux/modules/value/messages/messages.utils";
import BpostService, { ErrorField } from "@/services/bpost.service";
import { BPOSTInvalidAddressMessageId } from "@/utils/address/validation.config";
import {
    AddressToValidate,
    BusinessAddressToValidate
} from "@/utils/address/validation.types";

const InvalidAddressModal = lazy(() =>
    import(
        "@/components/Address/AddressValidation/components/InvalidAddressModal/invalidAddressModal.component"
    ).then((module) => ({ default: module.InvalidAddressModal }))
);
const CreateOrEditAddressModal = lazy(() =>
    import(
        "@/components/Address/modals/CreateOrEditAddressModal/createOrEditAddressModal.component"
    ).then((module) => ({ default: module.CreateOrEditAddressModal }))
);

export type ValidationAddress = {
    id?: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    street: string;
    number: string;
    zipCode: string;
    city: string;
    country: string;
    box?: string | null;
    addressing?: string | null;
    validationStatus: AddressValidationStatus;
};

export type AddressFormProviderValues = {
    errorFields: Array<ErrorField>;
    isValidating: boolean;
    validate: (
        addressToValidate: AddressToValidate
    ) => Promise<AddressValidationStatus>;
    validateBusiness: (
        businessAddressToValidate: BusinessAddressToValidate
    ) => Promise<AddressValidationStatus>;
    openAddressModal: (address?: any) => void;
    onAddressCreated?: (id: string) => void;
    onAddressEdited?: (id: string) => void;
};

export const AddressFormContext =
    createContext<AddressFormProviderValues | null>(null);

type UseAddressFormProps = {
    onAddressChange?: (addressId: string) => void;
};

export const useAddressForm = ({ onAddressChange }: UseAddressFormProps) => {
    const promiseRef = useRef<
        ((status: AddressValidationStatus) => void) | null
    >(null);

    const [isValidating, setIsValidating] = useState(false);
    const [errorFields, setErrorFields] = useState<Array<ErrorField>>([]);
    const [isEditAddressModalOpen, setIsEditAddresModalOpen] = useState(false);
    const [invalidAddressModal, setInvalidAddresModal] = useState<
        undefined | AddressSubject
    >(undefined);
    const [address, setAddress] = useState<ValidationAddress | null>();
    const [type, setType] = useState<AddressType | null>(null);

    useEffect(() => {
        if (!isEditAddressModalOpen) {
            clearMessageById(BPOSTInvalidAddressMessageId);
        }
    }, [isEditAddressModalOpen]);

    const clearState = useCallback(() => {
        setIsEditAddresModalOpen(false);
        setInvalidAddresModal(undefined);
        setAddress(null);
        setType(null);
        setIsValidating(false);
    }, []);

    const openAddressModal = useCallback((address?: any) => {
        setAddress(address);
        setIsEditAddresModalOpen(true);
    }, []);

    const validate = useCallback(
        async ({
            id,
            firstName,
            lastName,
            street,
            number,
            zipCode,
            city,
            country,
            box,
            addressing,
            type,
            isBpostField,
            currentStatus
        }: AddressToValidate) => {
            return new Promise<AddressValidationStatus>((resolve) => {
                setErrorFields([]);

                if (isBpostField) {
                    return resolve(AddressValidationStatus.Validated);
                }

                if (country !== Country.Be) {
                    return resolve(AddressValidationStatus.NoValidationNeeded);
                }

                if (
                    currentStatus === AddressValidationStatus.Validated ||
                    currentStatus ===
                        AddressValidationStatus.OverruledByCustomer ||
                    currentStatus === AddressValidationStatus.NoValidationNeeded
                ) {
                    return resolve(currentStatus);
                }

                setType(type || null);
                setIsValidating(true);

                BpostService.validateAddress({
                    street,
                    number,
                    zipCode,
                    city,
                    box
                }).then((validationResult) => {
                    setIsValidating(false);
                    promiseRef.current = resolve;

                    setErrorFields(validationResult.errorFields || []);

                    if (
                        validationResult.isValid &&
                        validationResult.newStatus
                    ) {
                        return resolve(validationResult.newStatus);
                    }

                    setAddress({
                        id,
                        firstName,
                        lastName,
                        street,
                        number,
                        zipCode,
                        city,
                        box,
                        country,
                        addressing,
                        validationStatus: AddressValidationStatus.Invalid
                    });

                    setIsEditAddresModalOpen(false);
                    setInvalidAddresModal(AddressSubject.Private);
                });
            });
        },
        []
    );

    const validateBusiness = useCallback(
        async ({
            companyName,
            street,
            number,
            zipCode,
            city,
            country,
            box,
            addressing,
            type,
            isBpostField,
            currentStatus
        }: BusinessAddressToValidate) => {
            return new Promise<AddressValidationStatus>((resolve) => {
                setErrorFields([]);

                if (isBpostField) {
                    return resolve(AddressValidationStatus.Validated);
                }

                if (country !== Country.Be) {
                    return resolve(AddressValidationStatus.NoValidationNeeded);
                }

                if (
                    currentStatus === AddressValidationStatus.Validated ||
                    currentStatus ===
                        AddressValidationStatus.OverruledByCustomer ||
                    currentStatus === AddressValidationStatus.NoValidationNeeded
                ) {
                    return resolve(currentStatus);
                }

                setType(type || null);
                setIsValidating(true);

                BpostService.validateAddress({
                    street,
                    number,
                    zipCode,
                    city,
                    box
                }).then((validationResult) => {
                    setIsValidating(false);
                    promiseRef.current = resolve;

                    setErrorFields(validationResult.errorFields || []);

                    if (
                        validationResult.isValid &&
                        validationResult.newStatus
                    ) {
                        return resolve(validationResult.newStatus);
                    }

                    setAddress({
                        companyName,
                        street,
                        number,
                        zipCode,
                        city,
                        box,
                        country,
                        addressing,
                        validationStatus: AddressValidationStatus.Invalid
                    });

                    setInvalidAddresModal(AddressSubject.Business);
                });
            });
        },
        []
    );

    const handleOnInvalidAddressModalEdit = useCallback(() => {
        setInvalidAddresModal(undefined);

        if (invalidAddressModal === "private") {
            setIsEditAddresModalOpen(true);
        }
    }, [invalidAddressModal]);

    const handleOnInvalidAddressModalClose = useCallback(() => {
        if (isEditAddressModalOpen) {
            setInvalidAddresModal(undefined);
        } else {
            setErrorFields([]);
            clearState();
        }
        promiseRef.current?.(AddressValidationStatus.Closed);
    }, [clearState, isEditAddressModalOpen]);

    const handleOnInvalidAddressModalConfirm = useCallback(() => {
        clearState();
        setErrorFields([]);
        promiseRef.current?.(AddressValidationStatus.OverruledByCustomer);
    }, [clearState]);

    const handleOnCreateOrEditAddressModalChange = useCallback(
        (addressId: string) => {
            onAddressChange?.(addressId);
            clearState();
            setErrorFields([]);
            promiseRef.current?.(AddressValidationStatus.Validated);
        },
        [clearState, onAddressChange]
    );
    const handleOnCreateOrEditAddressModalOpen = useCallback(() => {
        setErrorFields([]);
        clearState();
        promiseRef.current?.(AddressValidationStatus.Closed);
    }, [clearState]);

    const AddressFormModals = (
        <>
            <InvalidAddressModal
                business={invalidAddressModal === "business"}
                type={type}
                address={address}
                isOpen={!!invalidAddressModal}
                onEdit={handleOnInvalidAddressModalEdit}
                onClose={handleOnInvalidAddressModalClose}
                onConfirm={handleOnInvalidAddressModalConfirm}
            />
            <CreateOrEditAddressModal
                address={address}
                open={isEditAddressModalOpen}
                type={type}
                skipValidation={false}
                onChange={handleOnCreateOrEditAddressModalChange}
                setOpen={handleOnCreateOrEditAddressModalOpen}
            />
        </>
    );

    return {
        errorFields: errorFields,
        isValidating: isValidating,
        validate: validate,
        validateBusiness: validateBusiness,
        openAddressModal: openAddressModal,
        AddressFormModals: AddressFormModals
    };
};
